import React from 'react'
import styled from 'styled-components'
import { Button } from '@streamr/streamr-layout'
import { MONO, SANS, REGULAR, TABLET, DESKTOP } from '@streamr/streamr-layout/styled'
import background from './assets/background.png'
import DangerousHtml from '~shared/DangerousHtml'
import Container from '~shared/Container'
import H from '~shared/Hero'
import Display from '~shared/Display'
import useTranslation from '~utils/useTranslation'
import Nav from '~shared/Nav'
import Link from '~shared/Link'
import ButtonWrapper from '~shared/ButtonWrapper'
import urls from '~utils/urls'
import useNetworkStats from '~hooks/useNetworkStats'

const NetworkVisualisation = styled.div`
    background-color: #0B141F;
    height: 100%;
    width: 100%;
    overflow: hidden;

    img {
        position: absolute;
        top: 0;
        left: 50%;
        transform: translateX(-50%);
        width: 100%;
        min-width: ${TABLET}px;
    }
`

const Outer = styled.div`
    align-items: flex-end;
    display: flex;
    flex-direction: row;
    height: 100%;
    padding-bottom: 72px;
    box-sizing: border-box;
    position: relative;

    @media (min-width: ${TABLET}px) {
        padding-bottom: 136px;
    }

    @media (min-width: ${DESKTOP}px) {
        padding-bottom: 0;
        align-items: center;
    }
`

const Content = styled.div`
    @media (min-width: ${DESKTOP}px) {
        width: 50%;
        text-align: left;
        margin-left: auto;
        margin-right: 0;
        box-sizing: border-box;
        margin-top: 10.5rem;

        ${H.Headline} {
            text-align: left;
        }
    }
`

const Label = styled.div`
    font-family: ${MONO};
    font-weight: ${REGULAR};
    font-size: 0.625rem;
    line-height: 1.5rem;
    letter-spacing: 0.1em;
    margin-bottom: 0;

    @media (min-width: ${TABLET}px) {
        margin-bottom: 0.5rem;
        font-size: 0.875rem;
    }
`

const Value = styled.div`
    font-family: ${SANS};
    font-weight: ${REGULAR};
    font-size: 1.5rem;
    line-height: 2.25rem;
    letter-spacing: -0.02rem;

    @media (min-width: ${TABLET}px) {
        font-size: 2rem;
    }
`

const Stat = styled.div`
    white-space: nowrap;
`

const Stats = styled.div`
    margin-top: 2.5rem;
    display: flex;
    flex-direction: row;

    ${Stat} {
        min-width: 5rem;
    }

    ${Stat} + ${Stat} {
        margin-left: 1rem;
    }

    @media (min-width: ${TABLET}px) {
        margin-top: 5rem;
        margin-right: 6rem;

        ${Stat} {
            min-width: 9rem;
        }
    }

    @media (min-width: ${DESKTOP}px) {
        margin-top: 4rem;
        margin-right: 6rem;

        ${Stat} {
            min-width: 11rem;
        }
    }
`

const Actions = styled.div`
    display: flex;
    flex-direction: column;
    margin-top: 2.5rem;

    ${ButtonWrapper} + ${ButtonWrapper} {
        margin-top: 1rem;
    }

    @media (max-width: ${TABLET}px) {
        align-items: flex-start;

        ${Button} {
            width: 195px;
        }
    }

    @media (min-width: ${TABLET}px) {
        margin-top: 4rem;
        flex-direction: row;

        ${ButtonWrapper} + ${ButtonWrapper} {
            margin-top: 0;
            margin-left: 1rem;
        }
    }

    @media (min-width: ${DESKTOP}px) {
        margin-top: 5rem;
    }
`

const UnstyledHero = (props) => {
    const { t } = useTranslation()

    const { messagesPerSecond, numberOfNodes } = useNetworkStats()

    return (
        <div {...props}>
            <Nav light />
            <NetworkVisualisation>
                <img src={background} alt="" />
            </NetworkVisualisation>
            <Container>
                <Outer>
                    <Content>
                        <DangerousHtml tag={H.Headline} $appear>
                            {t('learn.network.hero.title')}
                        </DangerousHtml>
                        <div $appear>
                            <Display mobile="none" tablet="block">
                                <DangerousHtml tag={H.Paragraph}>
                                    {t('learn.network.hero.description.desktop')}
                                </DangerousHtml>
                            </Display>
                            <Display mobile="block" tablet="none">
                                <DangerousHtml tag={H.Paragraph}>
                                    {t('learn.network.hero.description.mobile')}
                                </DangerousHtml>
                            </Display>
                        </div>
                        <Stats>
                            <Stat $appear>
                                <Label>MSGS/SEC</Label>
                                <Value>{messagesPerSecond || '-'}</Value>
                            </Stat>
                            <Stat $appear>
                                <Label>
                                    {numberOfNodes === 1 ? 'NODE' : 'NODES'}
                                </Label>
                                <Value>{numberOfNodes || '-'}</Value>
                            </Stat>
                        </Stats>
                        <Actions>
                            <ButtonWrapper>
                                <Button
                                    tag="a"
                                    kind="primary"
                                    size="big"
                                    href={urls.docs.streamsIntro}
                                >
                                    Visit Network docs
                                </Button>
                            </ButtonWrapper>
                            <ButtonWrapper>
                                <Button
                                    tag={Link}
                                    kind="secondary"
                                    variant="light"
                                    size="big"
                                    href={urls.project.roadmap}
                                    outline
                                    noDecoration
                                    noHover
                                >
                                    Network Roadmap
                                </Button>
                            </ButtonWrapper>
                        </Actions>
                    </Content>
                </Outer>
            </Container>
        </div>
    )
}

const Hero = styled(UnstyledHero)`
    height: 880px;
    position: relative;
    color: #FFFFFF;

    ${Nav} {
        left: 0;
        position: absolute;
        top: 0;
        width: 100%;
        z-index: 2;
    }

    ${Container} {
        height: 100%;
        position: relative;
        z-index: 1;
    }

    ${NetworkVisualisation} {
        left: 0;
        position: absolute;
        top: 0;
        z-index: 0;
    }

    @media (min-width: ${TABLET}px) {
        height: 1072px;

        ${Container} {
            padding: 0;
            max-width: 520px;
        }
    }

    @media (min-width: ${DESKTOP}px) {
        height: 992px;

        ${Container} {
            max-width: 972px;
        }
    }
`

export default Hero
